import { useEffect, useRef, useState } from "react";
import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import { useInput } from "react-admin";
import {
  CONTRACT_BASIS_MAX_MONTHS,
  CONTRACT_BASIS_PAID_INCURRED,
  CONTRACT_BASIS_PRESETS,
  formatContractBasis,
  formatPaidBasis,
  isPaidBasis,
  isValidMonths,
  parseContractBasis,
  validateContractBasisPair,
} from "@/utils/contractBasis";

interface ContractBasisInputProps {
  source: string;
  label?: string | false;
  helperText?: string | false;
  /** Tight layout for in-place editing inside a grid cell. */
  compact?: boolean;
  /** Focus the incurred field on mount (in-place editor). */
  autoFocus?: boolean;
}

const PAID_CHIP_LABEL = "Paid (any incurred date)";
export const CONTRACT_BASIS_INCOMPLETE_MESSAGE = "Enter both the incurred and the paid months";

/** Splits a raw in-progress value ("24/", "/12", "24/12") into its two sides. */
const splitRaw = (value: unknown): [string, string] | null => {
  if (typeof value !== "string" || !value.includes("/")) return null;
  const [i, p] = value.split("/", 2);
  return [i ?? "", p ?? ""];
};

/**
 * Two-number input for the contract basis "N/M" (#7320, ADR-081 D5.2a).
 *
 * Binds to a single form field holding the canonical string. The user types the
 * incurred months and the paid months (or taps a preset); the form value becomes
 * "N/M", or "" when both are blank (unknown basis — the API stores NULL and reads
 * it as 12/12).
 *
 * A half-entered or out-of-range pair is written to the field as-is ("24/"), so
 * the field-level validator fails and react-admin blocks Save — the error is not
 * advisory (!4843 review). The field only ever holds "", a canonical "N/M", or an
 * in-progress "i/p" the validator rejects.
 *
 * A pure paid-basis contract ("Paid": no incurred restriction) is one chip. It
 * stores the API's 999 sentinel for the incurred side, shows "Paid" in its place,
 * and keeps the paid months editable. Typing an incurred number leaves Paid mode.
 *
 * Legacy wording that is not canonical shows as blank fields with a note, so
 * saving replaces it rather than round-tripping prose.
 */
export const ContractBasisInput = ({
  source,
  label = "Contract Basis",
  helperText,
  compact = false,
  autoFocus = false,
}: ContractBasisInputProps) => {
  const {
    field,
    fieldState: { error, isTouched },
    id,
  } = useInput({
    source,
    validate: (value: unknown) => {
      if (value === "" || value === null || value === undefined) return undefined;
      if (parseContractBasis(String(value))) return undefined;
      const raw = splitRaw(value);
      if (raw) return validateContractBasisPair(raw[0], raw[1]) ?? CONTRACT_BASIS_INCOMPLETE_MESSAGE;
      return "Enter the basis as incurred and paid months";
    },
  });

  // The value the record arrived with: the only one that counts as "legacy wording".
  const initialValue = useRef(field.value);

  const parsed = parseContractBasis(field.value as string | null | undefined);
  // Paid mode holds while the incurred side is the sentinel, including the
  // in-progress "999/" written when the paid box is cleared — otherwise 999 would
  // surface in an editable incurred box (!4843 review).
  const paidMode = isPaidBasis(parsed) || splitRaw(field.value)?.[0] === String(CONTRACT_BASIS_PAID_INCURRED);
  const legacyWording =
    !parsed &&
    field.value === initialValue.current &&
    typeof field.value === "string" &&
    field.value.trim() !== "" &&
    !splitRaw(field.value);

  const [incurred, setIncurred] = useState(parsed && !paidMode ? String(parsed.incurred) : "");
  const [paid, setPaid] = useState(parsed ? String(parsed.paid) : "");

  // Follow external changes to the form value (preset tap, reset, record load).
  // An in-progress "i/p" is our own write: mirror it back so typing is not wiped.
  useEffect(() => {
    const v = field.value as string | null | undefined;
    const p = parseContractBasis(v);
    if (p) {
      setIncurred(isPaidBasis(p) ? "" : String(p.incurred));
      setPaid(String(p.paid));
      return;
    }
    const raw = splitRaw(v);
    if (raw) {
      setIncurred(raw[0] === String(CONTRACT_BASIS_PAID_INCURRED) ? "" : raw[0]);
      setPaid(raw[1]);
      return;
    }
    setIncurred("");
    setPaid("");
  }, [field.value]);

  // In Paid mode only the paid side can be wrong; show the same message the
  // validator raises so the blocked Save is explained.
  const pairError = paidMode
    ? paid.trim() === ""
      ? CONTRACT_BASIS_INCOMPLETE_MESSAGE
      : undefined
    : validateContractBasisPair(incurred, paid);

  // Writes the pair to the field: canonical when complete, "" when both blank,
  // otherwise the raw partial so validation blocks the save.
  const commit = (nextIncurred: string, nextPaid: string) => {
    const i = nextIncurred.trim();
    const p = nextPaid.trim();
    if (i === "" && p === "") {
      field.onChange("");
      return;
    }
    if (validateContractBasisPair(i, p) === undefined) {
      field.onChange(formatContractBasis(Number(i), Number(p)));
      return;
    }
    field.onChange(`${i}/${p}`);
  };

  const onIncurred = (v: string) => {
    setIncurred(v);
    commit(v, paid);
  };
  const onPaid = (v: string) => {
    setPaid(v);
    if (paidMode) {
      const n = Number(v.trim());
      // Keep the sentinel on the incurred side while the paid months are invalid, so Paid
      // mode survives a cleared or out-of-range paid box (!4843 thread 29878).
      field.onChange(isValidMonths(n) ? formatPaidBasis(n) : `${CONTRACT_BASIS_PAID_INCURRED}/${v.trim()}`);
      return;
    }
    commit(incurred, v);
  };
  const onPreset = (preset: string) => {
    field.onChange(preset);
  };
  const onPaidChip = () => {
    if (paidMode) {
      // Leave Paid mode keeping the paid months; the incurred side is now owed.
      commit("", paid);
      return;
    }
    const n = Number(paid.trim());
    field.onChange(formatPaidBasis(isValidMonths(n) ? n : 12));
  };

  const shownError = pairError ?? (isTouched && error?.message ? error.message : undefined);
  const size = compact ? "small" : "medium";
  const numberProps = { inputMode: "numeric" as const, min: 1, max: CONTRACT_BASIS_MAX_MONTHS, step: 1 };

  return (
    <Box data-testid={`contract-basis-input-${source}`} sx={{ mb: compact ? 0 : 2.5 }}>
      {label && !compact && (
        <Typography variant="caption" color="text.secondary" component="label" htmlFor={`${id}-incurred`}>
          {label}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: compact ? 0 : 0.5 }}>
        {paidMode ? (
          <TextField
            id={`${id}-incurred`}
            label={compact ? undefined : "Incurred"}
            value="Paid"
            size={size}
            variant={compact ? "standard" : "outlined"}
            disabled
            slotProps={{ htmlInput: { "aria-label": "Incurred: any date (paid basis)" } }}
            sx={{ width: compact ? 72 : 150 }}
          />
        ) : (
          <TextField
            id={`${id}-incurred`}
            label={compact ? undefined : "Incurred (months)"}
            placeholder="12"
            type="number"
            size={size}
            variant={compact ? "standard" : "outlined"}
            value={incurred}
            onChange={(e) => onIncurred(e.target.value)}
            onBlur={field.onBlur}
            autoFocus={autoFocus}
            error={!!shownError}
            slotProps={{ htmlInput: { ...numberProps, "aria-label": "Incurred months" } }}
            sx={{ width: compact ? 64 : 150 }}
          />
        )}
        <Typography variant="h6" component="span" color="text.secondary">
          /
        </Typography>
        <TextField
          id={`${id}-paid`}
          label={compact ? undefined : "Paid (months)"}
          placeholder="12"
          type="number"
          size={size}
          variant={compact ? "standard" : "outlined"}
          value={paid}
          onChange={(e) => onPaid(e.target.value)}
          onBlur={field.onBlur}
          error={!!shownError}
          slotProps={{ htmlInput: { ...numberProps, "aria-label": "Paid months" } }}
          sx={{ width: compact ? 64 : 150 }}
        />
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.5 }}>
        {CONTRACT_BASIS_PRESETS.map((preset) => (
          <Chip
            key={preset}
            label={preset}
            size="small"
            variant={field.value === preset ? "filled" : "outlined"}
            color={field.value === preset ? "primary" : "default"}
            onClick={() => onPreset(preset)}
          />
        ))}
        <Chip
          label={PAID_CHIP_LABEL}
          size="small"
          variant={paidMode ? "filled" : "outlined"}
          color={paidMode ? "primary" : "default"}
          onClick={onPaidChip}
          data-testid={`contract-basis-paid-${source}`}
        />
      </Stack>
      {(shownError || legacyWording || (helperText && !compact)) && (
        <Typography
          variant="caption"
          color={shownError ? "error" : "text.secondary"}
          sx={{ display: "block", mt: 0.5 }}
        >
          {shownError ??
            (legacyWording
              ? `Stored as "${String(field.value)}" — enter the months to replace it`
              : (helperText as string))}
        </Typography>
      )}
    </Box>
  );
};

export default ContractBasisInput;
